// What a note's state says to its writer (module 10 U209; pipeline I2). Plain words, nothing about how it is read.
import { dayMonthTime } from '../day.js';
import type { Note, Uploaded } from './api.js';

/** "5 Oct, 00:10" in the reader's own zone, spelled by day.ts as every page spells a day. */
export function whenWords(iso: string): string {
  return dayMonthTime(new Date(iso));
}

/** The state line under a note's name, and whether it is a warning. */
export function noteStateWords(n: Pick<Note, 'state' | 'reason' | 'kind' | 'readAt' | 'pages'>): { text: string; tone: 'plain' | 'warn' } {
  switch (n.state) {
    case 'read': return { text: n.pages && n.pages > 1 ? `Read, ${n.pages} pages` : 'Read', tone: 'plain' };
    case 'reading': return { text: 'Being read', tone: 'plain' };
    case 'waiting': return { text: n.kind === 'audio' ? 'Kept; audio notes are not read yet' : 'Waiting to be read', tone: 'plain' };
    case 'duplicate': return { text: `Not added again: ${n.reason ?? 'the same file was uploaded before'}`, tone: 'plain' };
    case 'refused': return { text: `Not read: ${n.reason ?? 'the page could not be read'}`, tone: 'warn' };
    case 'failed': return { text: `Not read: ${(n.reason ?? '').replace(/^not read:\s*/i, '') || 'reading it kept failing'}. Upload it again later.`, tone: 'warn' };
  }
}

/** The line an upload leaves beside the button, for one file. */
export function uploadedWords(name: string, u: Uploaded): { text: string; tone: 'plain' | 'warn' } {
  if (!u.ok) return { text: `${name}: ${u.error}`, tone: 'warn' };
  switch (u.state) {
    case 'read': return { text: `${name}: read`, tone: 'plain' };
    case 'waiting': return { text: u.kind === 'audio' ? `${name}: kept; audio notes are not read yet` : `${name}: added, being read`, tone: 'plain' };
    case 'duplicate': return { text: `${name}: not added again, ${u.reason}`, tone: 'plain' };
    case 'refused': return { text: `${name}: not read, ${u.reason}`, tone: 'warn' };
  }
}

/**
 * The words split so the ones to check can be marked where they stand: each unsure word is marked at its first place
 * in the text not already marked. A word not found in the text is left for the list under it.
 */
export function markedParts(text: string, unsure: readonly { text: string }[]): Array<{ text: string; unsure: boolean }> {
  const spans: Array<[number, number]> = [];
  for (const u of unsure) {
    const w = u.text.trim();
    if (!w) continue;
    let from = 0;
    for (;;) {
      const i = text.indexOf(w, from);
      if (i < 0) break;
      const end = i + w.length;
      if (!spans.some(([a, b]) => i < b && end > a)) { spans.push([i, end]); break; }
      from = i + 1;
    }
  }
  spans.sort((a, b) => a[0] - b[0]);
  const out: Array<{ text: string; unsure: boolean }> = [];
  let at = 0;
  for (const [a, b] of spans) {
    if (a > at) out.push({ text: text.slice(at, a), unsure: false });
    out.push({ text: text.slice(a, b), unsure: true });
    at = b;
  }
  if (at < text.length) out.push({ text: text.slice(at), unsure: false });
  return out;
}

/** The unsure words that could not be marked in the text. */
export function unmarked(text: string, unsure: readonly { text: string; page: number }[]): { text: string; page: number }[] {
  return unsure.filter((u) => u.text.trim() !== '' && !text.includes(u.text.trim()));
}
